import { isDevelopment } from "appConfiguration";
import type { BrandTwoLetterCode, RegionCode } from "brandRegionConfig";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { AppBar, Box, Stack, Toolbar, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import {
  type PublicAppConfiguration,
  PublicConfigurationProvider,
} from "core/context/AppConfigurationContext";
import { useInitializeGlobals } from "core/hooks/useInitializeGlobals";
import { copyTranslationsToClipboard } from "i18n.config";
import type { NextComponentType, NextPageContext } from "next";
import { useRouter } from "next/router";
import { BrandLogo } from "./BrandLogo";
import { PageErrorBoundary } from "./PageErrorBoundary";
import { PublicLanguageSelect } from "./PublicLanguageSelect";
import { SharedAppWrapper } from "./SharedAppWrapper";

export type PublicApplicationContext = {
  applicationId: "public";
  brandTwoLetterCode: BrandTwoLetterCode;
  availableRegions: RegionCode[];
  environmentVariables: PublicAppConfiguration;
  requestInfo: {
    origin: string;
    host: string;
  };
};

/**
 * PublicApp is an internal Hub application component that serves as the main
 * entry point for the Public application. Currently used for the public Estimate Request page, and any future public pages.
 */
export function PublicApp({
  Component,
  pageProps,
  applicationContext,
}: {
  Component: NextComponentType<NextPageContext, any, any>;
  pageProps: any;
  applicationContext: PublicApplicationContext;
}) {
  const { pathname } = useRouter();
  useInitializeGlobals({
    centrixApiUrl: applicationContext.environmentVariables.API_URL!,
    appInsightsConnectionString:
      applicationContext.environmentVariables
        .APPLICATIONINSIGHTS_CONNECTION_STRING!,
  });

  return (
    <PublicConfigurationProvider
      config={applicationContext.environmentVariables}
    >
      <SharedAppWrapper
        brandTwoLetterCode={applicationContext.brandTwoLetterCode}
      >
        <AppBar
          position="sticky"
          sx={{
            mb: 2,
            pt: 1,
            display: pathname === "/public/nps" ? "none" : "block",
          }}
        >
          <Toolbar
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) 2fr minmax(0,1fr)",
            }}
          >
            <div /> {/* fill slot */}
            <Box width="min(175px, 100%)" justifySelf="center">
              <BrandLogo logoStyle="Mobile" />
            </Box>
            <Stack direction="row" justifySelf="end">
              {isDevelopment && (
                <Tooltip title="Copy translations to clipboard">
                  <Button color="inherit" onClick={copyTranslationsToClipboard}>
                    <ContentPasteIcon />
                  </Button>
                </Tooltip>
              )}
              <PublicLanguageSelect />
            </Stack>
          </Toolbar>
        </AppBar>
        <PageErrorBoundary>
          <Component {...pageProps} />
        </PageErrorBoundary>
      </SharedAppWrapper>
    </PublicConfigurationProvider>
  );
}
